import NovoProcessoManutencao from '../components/NovoProcessoManutencao';
import ManutencaoLinkExterno from '../components/ManutencaoLinkExterno';
import { useAuth } from '../context/AuthContext';
import ManutencaoEvidencia, { eVideo } from '../components/ManutencaoEvidencia';
import MaintenanceTimeline from '../components/MaintenanceTimeline';
import LiquidText from '../components/LiquidText';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useMaquetesTheme } from '../context/MaquetesThemeContext';
import { OrcamentoDialog, OrcamentoIcon as Icon, OrcamentoFeedbackProvider, useOrcamentoFeedback } from '../components/OrcamentoUI';
import './ManutencoesTheme.css';

const MaintenanceDialog = ({ children, onClose }) => {
    const { theme } = useMaquetesTheme();
    return <OrcamentoDialog data-maintenance-dialog="true" style={theme === 'liquido' ? undefined : overlay} onClose={onClose} label="Manutenção">{children}</OrcamentoDialog>;
};

/* ============================================================
   MANUTENÇÃO (desktop) — listagem das obras de manutenção,
   acesso ao processo, fichas geradas (PDF guardado), download
   e edição dos dados de cada ficha.
   ============================================================ */

// O estado é um só — o do processo (pendência/manutenção); as fichas apenas o
// despoletam quando são gravadas.
const ESTADO_OBRA = {
    planeada: { label: 'Planeada', bg: '#fef9c3', fg: '#854d0e' },
    em_curso: { label: 'Em curso', bg: '#e0f2fe', fg: '#075985' },
    parcial: { label: 'Parcialmente resolvida', bg: '#ffedd5', fg: '#9a3412' },
    concluida: { label: 'Concluída', bg: '#dcfce7', fg: '#166534' },
    suspensa: { label: 'Suspensa', bg: '#f1f5f9', fg: '#64748b' },
    recebida: { label: 'Recebida', bg: '#ede9fe', fg: '#5b21b6' },
};
const fmtData = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return '—'; } };
// 'YYYY-MM-DD' no fuso local, para um <input type="date">: a data de conclusão
// vem como timestamp e cortar a string ISO podia dar o dia anterior.
const diaInput = (d) => {
    if (!d) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(d))) return String(d);
    const x = new Date(d);
    if (Number.isNaN(x.getTime())) return '';
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
const hojeLocal = () => diaInput(new Date());
const horaCurta = (h) => (h ? String(h).slice(0, 5) : '');
/* Início e fim da intervenção: as mesmas regras do servidor — tudo preenchido
   e o fim depois do início (o fim pode ser noutro dia). */
const erroPeriodo = (diaIni, hIni, diaFim, hFim) => {
    if (!diaIni || !hIni) return 'Indique o dia e a hora de início da intervenção.';
    if (!hFim) return 'Indique a hora de fim da intervenção.';
    const fim = diaFim || diaIni;
    if (fim < diaIni) return 'O dia de fim da intervenção não pode ser anterior ao de início.';
    if (`${fim}T${hFim}` <= `${diaIni}T${hIni}`) return 'O fim da intervenção tem de ser depois do início.';
    return null;
};
const fmtDataCurta = (d) => { if (!d) return '—'; try { const x = new Date(d); return `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}`; } catch { return '—'; } };
// Visto de resolução na barra: Concluída = verde (totalmente resolvido); Parcial = amarelo.
const resolucaoInfo = (status) => {
    if (status === 'concluida') return { label: 'Totalmente resolvido', color: '#16a34a', bg: '#dcfce7' };
    if (status === 'parcial') return { label: 'Parcialmente resolvido', color: '#ca8a04', bg: '#fef9c3' };
    return null;
};

const MAX_FOTOS = 6;
// Meios de acesso/elevação para trabalhos em altura (sugestões no editor de secretária)
const MEIOS_ELEVATORIOS = [
    'Escada de mão / escadote',
    'Andaime fixo',
    'Andaime móvel (torre)',
    'Plataforma elevatória — tesoura',
    'Plataforma elevatória — articulada/telescópica',
    'Plataforma sobre veículo',
    'Cesto / plataforma suspensa',
];
// Servir as fotos guardadas (ficheiros em /uploads, fora do /api)
const uploadsBase = api.defaults.baseURL.replace(/\/api\/?$/, '');

// Mesma compressão do mobile: máx. 1280px, JPEG q0.7 — evita mandar 8 MB por foto
const comprimirImagem = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
            const maxDim = 1280;
            let { width, height } = img;
            if (Math.max(width, height) > maxDim) {
                const escala = maxDim / Math.max(width, height);
                width = Math.round(width * escala);
                height = Math.round(height * escala);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width; canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = () => resolve(reader.result);
        img.src = reader.result;
    };
    reader.readAsDataURL(file);
});

/* ---------- Editar ficha ---------- */
function FichaModal({ ficha, onClose, onSaved }) {
    const { notify } = useOrcamentoFeedback();
    const [form, setForm] = useState({
        data_intervencao: ficha.data_intervencao ? String(ficha.data_intervencao).substring(0, 10) : '',
        data_fim_intervencao: String(ficha.data_fim_intervencao || ficha.data_intervencao || '').substring(0, 10),
        hora_inicio: ficha.hora_inicio ? String(ficha.hora_inicio).substring(0, 5) : '',
        hora_fim: ficha.hora_fim ? String(ficha.hora_fim).substring(0, 5) : '',
        // material: uma linha por item, "descrição | quantidade"
        materiais: (Array.isArray(ficha.materiais) ? ficha.materiais : []).map(m => m.quantidade ? `${m.descricao} | ${m.quantidade}` : m.descricao).join('\n'),
        diagnostico: ficha.diagnostico || '',
        resolucao: ficha.resolucao || '',
        comentarios: ficha.comentarios || '',
        trabalhos: (Array.isArray(ficha.trabalhos_executados) ? ficha.trabalhos_executados : []).join('\n'),
        nome_tecnico: ficha.nome_tecnico || '',
        nome_responsavel: ficha.nome_responsavel || '',
        meio_elevatorio: ficha.meio_elevatorio || '',
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
    // O dia de fim acompanha o de início enquanto for o mesmo dia
    const mudarDiaInicio = (v) => setForm(prev => ({
        ...prev,
        data_intervencao: v,
        data_fim_intervencao: (!prev.data_fim_intervencao || prev.data_fim_intervencao === prev.data_intervencao || prev.data_fim_intervencao < v)
            ? v : prev.data_fim_intervencao,
    }));

    // Fotos: as que já estão no servidor (caminhos) e as novas (base64)
    const [fotosExistentes, setFotosExistentes] = useState(() => {
        try { const v = typeof ficha.fotos === 'string' ? JSON.parse(ficha.fotos || '[]') : (ficha.fotos || []); return Array.isArray(v) ? v : []; }
        catch { return []; }
    });
    const [fotosNovas, setFotosNovas] = useState([]);
    const fotoInputRef = useRef(null);

    const juntarFotos = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        const jaTem = fotosExistentes.length + fotosNovas.length;
        if (jaTem + files.length > MAX_FOTOS) {
            setErr(`No máximo ${MAX_FOTOS} fotos por ficha (tem ${jaTem}).`);
            return;
        }
        setErr('');
        for (const file of files) {
            const b64 = await comprimirImagem(file);
            setFotosNovas(prev => [...prev, b64]);
        }
    };

    const guardar = async () => {
        // O relatório sai sempre com início e fim (dia e hora)
        const erroPer = erroPeriodo(form.data_intervencao, form.hora_inicio, form.data_fim_intervencao, form.hora_fim);
        if (erroPer) { setErr(erroPer); return; }
        setSaving(true); setErr('');
        try {
            // Sem "estado" no pedido: o estado é do processo e muda-se na lista
            await api.put(`/manutencao/${ficha.id}`, {
                data_intervencao: form.data_intervencao || null,
                data_fim_intervencao: form.data_fim_intervencao || form.data_intervencao || null,
                hora_inicio: form.hora_inicio || null,
                hora_fim: form.hora_fim || null,
                materiais: form.materiais.split('\n').map(l => l.trim()).filter(Boolean).map(l => {
                    const [descricao, quantidade] = l.split('|').map(s => s.trim());
                    return { descricao, quantidade: quantidade || '' };
                }),
                diagnostico: form.diagnostico,
                resolucao: form.resolucao,
                comentarios: form.comentarios,
                trabalhos_executados: form.trabalhos.split('\n').map(t => t.trim()).filter(Boolean),
                nome_tecnico: form.nome_tecnico,
                nome_responsavel: form.nome_responsavel,
                meio_elevatorio: form.meio_elevatorio || null,
                fotos_manter: fotosExistentes,   // as removidas ficam de fora
                fotos_base64: fotosNovas,
                origem: window.location.origin, // base do link no email da pendência resolvida
            }, { timeout: 60000 }).then(async r => {
                if (r.data?.email_aviso) await notify(`✉️ ${r.data.email_aviso}`);
            });
            onSaved(); onClose();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao guardar'); }
        finally { setSaving(false); }
    };

    return (
        <MaintenanceDialog onClose={onClose}>
            <div className="modal-ef mnt-modal mnt-edit-dialog" style={{ ...modal, maxWidth: 640 }} onClick={e => e.stopPropagation()}>
                <div className="modal-ef-header" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Editar ficha {ficha.relatorio_num}</h3>
                    <button onClick={onClose} aria-label="Fechar" className="modal-close" style={closeBtn}><Icon name="close" fallback="✕" /></button>
                </div>
                <div className="mnt-modal-body">
                <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: 10 }}>
                    Ao guardar, o PDF arquivado é regenerado com os novos dados. As assinaturas e fotos originais mantêm-se.
                </div>
                {/* Início e fim com dia e hora — saem sempre no relatório */}
                <div className="mnt-period-grid" style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1.3fr 1fr', gap: 10, marginBottom: 10 }}>
                    <div className="modal-field">
                        <label style={lbl}>Início — dia *</label>
                        <input type="date" value={form.data_intervencao} onChange={e => mudarDiaInicio(e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div className="modal-field">
                        <label style={lbl}>Hora *</label>
                        <input type="time" value={form.hora_inicio} onChange={e => upd('hora_inicio', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div className="modal-field">
                        <label style={lbl}>Fim — dia *</label>
                        <input type="date" value={form.data_fim_intervencao} min={form.data_intervencao || undefined}
                            onChange={e => upd('data_fim_intervencao', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div className="modal-field">
                        <label style={lbl}>Hora *</label>
                        <input type="time" value={form.hora_fim} onChange={e => upd('hora_fim', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div className="mnt-grid-2" style={grid2}>
                    <div className="modal-field">
                        <label style={lbl}>Técnico</label>
                        <input value={form.nome_tecnico} onChange={e => upd('nome_tecnico', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div className="modal-field">
                        <label style={lbl}>Responsável de obra</label>
                        <input value={form.nome_responsavel} onChange={e => upd('nome_responsavel', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div className="modal-field" style={{ marginBottom: 10 }}>
                    <label style={lbl}>Trabalhos executados (um por linha)</label>
                    <textarea rows={3} value={form.trabalhos} onChange={e => upd('trabalhos', e.target.value)} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div className="modal-field" style={{ marginBottom: 10 }}>
                    <label style={lbl}>Meio elevatório (trabalhos em altura)</label>
                    <input list="meios-elevatorios-list" value={form.meio_elevatorio}
                        onChange={e => upd('meio_elevatorio', e.target.value)}
                        placeholder="— sem trabalhos em altura —" style={{ ...inp, width: '100%' }} />
                    <datalist id="meios-elevatorios-list">
                        {MEIOS_ELEVATORIOS.map(m => <option key={m} value={m} />)}
                    </datalist>
                </div>
                <div className="modal-field" style={{ marginBottom: 10 }}>
                    <label style={lbl}>Material utilizado (um por linha — descrição | quantidade)</label>
                    <textarea rows={3} value={form.materiais} onChange={e => upd('materiais', e.target.value)} placeholder={'Fita LED 5 m | 2\nParafusos M6 | 20'} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div className="modal-field" style={{ marginBottom: 10 }}>
                    <label style={lbl}>Diagnóstico</label>
                    <textarea rows={2} value={form.diagnostico} onChange={e => upd('diagnostico', e.target.value)} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div className="modal-field" style={{ marginBottom: 10 }}>
                    <label style={lbl}>Resolução / trabalhos efetuados</label>
                    <textarea rows={2} value={form.resolucao} onChange={e => upd('resolucao', e.target.value)} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div className="modal-field" style={{ marginBottom: 10 }}>
                    <label style={lbl}>Comentários</label>
                    <textarea rows={2} value={form.comentarios} onChange={e => upd('comentarios', e.target.value)} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>

                {/* Fotos: remover as que não servem, juntar novas do computador */}
                <div className="modal-field" style={{ marginBottom: 10 }}>
                    <label style={lbl}>
                        Evidências ({fotosExistentes.length + fotosNovas.length}/{MAX_FOTOS})
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                        {fotosExistentes.map((p, i) => (
                            <div key={p} style={{ position: 'relative' }}>
                                {eVideo(p) ? <ManutencaoEvidencia src={`${uploadsBase}${p}`} alt={`Vídeo ${i + 1}`} style={{ borderRadius: 8 }} /> : (
                                <a href={`${uploadsBase}${p}`} target="_blank" rel="noreferrer" title="Abrir em tamanho real">
                                    <img src={`${uploadsBase}${p}`} alt={`Foto ${i + 1}`}
                                        style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 8, border: '1px solid #cbd5e1', display: 'block' }} />
                                </a>)}
                                <button type="button" title="Remover esta evidência"
                                    onClick={() => setFotosExistentes(fotosExistentes.filter(x => x !== p))}
                                    className="mnt-photo-remove" style={fotoRemover}><DeleteIcon /></button>
                            </div>
                        ))}
                        {fotosNovas.map((b64, i) => (
                            <div key={`nova-${i}`} style={{ position: 'relative' }}>
                                <img src={b64} alt={`Nova ${i + 1}`}
                                    style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 8, border: '2px solid #22c55e', display: 'block' }} />
                                <button type="button" title="Remover"
                                    onClick={() => setFotosNovas(fotosNovas.filter((_, j) => j !== i))}
                                    className="mnt-photo-remove" style={fotoRemover}><DeleteIcon /></button>
                            </div>
                        ))}
                        {fotosExistentes.length + fotosNovas.length === 0 && (
                            <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Sem fotos nesta ficha.</span>
                        )}
                    </div>
                    <input ref={fotoInputRef} type="file" accept="image/*" multiple onChange={juntarFotos} style={{ display: 'none' }} />
                    <button type="button" className="mnt-btn mnt-secondary" style={btnSec}
                        disabled={fotosExistentes.length + fotosNovas.length >= MAX_FOTOS}
                        onClick={() => fotoInputRef.current?.click()}><Icon name="camera" fallback="📷" /> Juntar fotos</button>
                    <span style={{ marginLeft: 10, fontSize: '0.78rem', color: '#94a3b8' }}>
                        As alterações às fotos só ficam gravadas ao carregar em Guardar.
                    </span>
                </div>

                {err && <div style={{ color: '#e53e3e', margin: '10px 0', fontSize: '0.85rem' }}>{err}</div>}
                </div>
                <div className="modal-form-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 12 }}>
                    <button onClick={async () => {
                        try {
                            const res = await api.get(`/manutencao/relatorio/${ficha.id}`, { responseType: 'blob' });
                            window.open(window.URL.createObjectURL(res.data), '_blank');
                        } catch { notify('Erro ao abrir o PDF'); }
                    }} className="mnt-btn mnt-secondary" style={btnSec} title="Ver o PDF arquivado"><Icon name="document" fallback="📄" /> PDF</button>
                    <div data-liquid-actions="true" style={{ display: 'flex', gap: 8 }}>
                        <button onClick={onClose} className="mnt-btn mnt-secondary" style={btnSec}>Cancelar</button>
                        <button onClick={guardar} disabled={saving} className="mnt-btn mnt-primary" style={{ ...btnPri, opacity: saving ? 0.6 : 1 }} title="Guardar e regenerar o PDF arquivado">{saving ? 'A guardar…' : 'Guardar'}</button>
                    </div>
                </div>
            </div>
        </MaintenanceDialog>
    );
}

/* ---------- Enviar relatório aos intervenientes ---------- */
function EnviarModal({ ficha, obra, onClose }) {
    const { notify } = useOrcamentoFeedback();
    const [cliente, setCliente] = useState(true);
    const [fiscalizacao, setFiscalizacao] = useState(true);
    const [extras, setExtras] = useState([]);
    const [novo, setNovo] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [err, setErr] = useState('');

    const juntar = () => {
        const e = novo.trim();
        if (!e) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setErr('Email inválido.'); return; }
        if (!extras.includes(e)) setExtras([...extras, e]);
        setNovo(''); setErr('');
    };

    const enviar = async () => {
        setEnviando(true); setErr('');
        try {
            const r = await api.post(`/manutencao/${ficha.id}/enviar`, {
                enviar_cliente: cliente, enviar_fiscalizacao: fiscalizacao, emails_extra: extras,
            });
            await notify(`✉️ Relatório ${ficha.relatorio_num} enviado a:\n\n${r.data.destinatarios.join('\n')}`);
            onClose();
        } catch (e) { setErr(e.response?.data?.error || 'Falha ao enviar'); }
        finally { setEnviando(false); }
    };

    const linha = (marcado, setMarcado, titulo, nome, email) => (
        <label className="mnt-recipient" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', opacity: email ? 1 : 0.5 }}>
            <input type="checkbox" checked={marcado && !!email} disabled={!email}
                onChange={e => setMarcado(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
                <strong style={{ fontSize: '0.88rem' }}><LiquidText>{titulo}</LiquidText></strong><br />
                <small style={{ color: '#64748b' }}>{email ? `${nome ? nome + ' · ' : ''}${email}` : 'sem email definido no processo'}</small>
            </span>
        </label>
    );

    const eo = ESTADO_OBRA[obra?.status] || ESTADO_OBRA.planeada;

    return (
        <MaintenanceDialog onClose={onClose}>
            <div className="modal-ef mnt-modal" style={{ ...modal, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
                <div className="modal-ef-header" style={modalHead}>
                    <h3 style={{ margin: 0 }}><Icon name="mail" fallback="✉️" /> Enviar relatório {ficha.relatorio_num}</h3>
                    <button onClick={onClose} aria-label="Fechar" className="modal-close" style={closeBtn}><Icon name="close" fallback="✕" /></button>
                </div>
                <div className="mnt-modal-body">
                <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 10 }}>
                    O texto do email segue o estado da {obra?.tipo_obra === 'pendencia' ? 'pendência' : 'manutenção'}:{' '}
                    <span className="mnt-state mnt-pill" data-state={obra?.status} style={{ ...badge, background: eo.bg, color: eo.fg }}><LiquidText>{eo.label}</LiquidText></span>
                    {!['concluida', 'recebida'].includes(obra?.status) && (
                        <div style={{ marginTop: 6, color: '#b45309' }}>
                            Se o trabalho está resolvido, mude o estado na lista antes de enviar.
                        </div>
                    )}
                </div>

                {linha(cliente, setCliente, 'Responsável do cliente', obra?.nome_cliente, obra?.email_cliente)}
                {linha(fiscalizacao, setFiscalizacao, 'Fiscalização', obra?.nome_fiscalizacao, obra?.email_fiscalizacao)}

                <div className="modal-field" style={{ marginTop: 8 }}>
                    <label style={lbl}>Outros emails</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input type="email" value={novo} placeholder="email@exemplo.pt"
                            onChange={e => setNovo(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); juntar(); } }}
                            style={{ ...inp, flex: 1 }} />
                        <button aria-label="Adicionar email" onClick={juntar} className="mnt-btn mnt-secondary" style={btnSec}><Icon name="plus" fallback="➕" /></button>
                    </div>
                    {extras.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                            {extras.map(em => (
                                <span key={em} className="mnt-pill" style={{ ...badge, background: '#edf2f7', color: '#334155', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                                    {em}
                                    <button onClick={() => setExtras(extras.filter(x => x !== em))}
                                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#e53e3e', fontWeight: 'bold', padding: 0 }}>×</button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {err && <div style={{ color: '#e53e3e', margin: '10px 0', fontSize: '0.85rem' }}>{err}</div>}
                </div>
                <div className="modal-form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                    <button onClick={onClose} className="mnt-btn mnt-secondary" style={btnSec}>Cancelar</button>
                    <button onClick={enviar} disabled={enviando} className="mnt-btn mnt-primary" style={{ ...btnPri, opacity: enviando ? 0.6 : 1 }}>
                        <Icon name="mail" fallback="✉️" /> {enviando ? 'A enviar…' : 'Enviar com PDF'}
                    </button>
                </div>
            </div>
        </MaintenanceDialog>
    );
}

/* ---------- Página principal ---------- */
const ManutencoesContent = () => {
    const { hasPermission } = useAuth();
    const [novoTipo, setNovoTipo] = useState(null);
    const [criadoMensagem, setCriadoMensagem] = useState('');
    const { notify, confirm } = useOrcamentoFeedback();
    const { theme } = useMaquetesTheme();
    const navigate = useNavigate();
    const [obras, setObras] = useState([]);
    const [fichas, setFichas] = useState({}); // obra_id -> fichas[]
    const [aberta, setAberta] = useState(null); // obra_id expandida
    const [loading, setLoading] = useState(true);
    const [pesquisa, setPesquisa] = useState('');
    /* Estados a mostrar. Por omissão os que estão por fechar — é a vista com que
       esta página sempre abriu. Vem tudo do servidor de uma vez e filtra-se aqui:
       são poucas dezenas de processos, e assim marcar ou desmarcar um estado é
       imediato em vez de ir buscar a lista outra vez. */
    const ABERTOS = Object.keys(ESTADO_OBRA).filter(k => k !== 'concluida' && k !== 'recebida');
    const [estadosFiltro, setEstadosFiltro] = useState(() => new Set(ABERTOS));
    const alternarEstado = (k) => setEstadosFiltro(prev => {
        const s = new Set(prev);
        if (s.has(k)) { s.delete(k); } else { s.add(k); }
        return s;
    });
    const [editarFicha, setEditarFicha] = useState(null);
    const [ordemLinkExterno, setOrdemLinkExterno] = useState(null);
    const [enviarFicha, setEnviarFicha] = useState(null); // { ficha, obra }
    const [tipoFiltro, setTipoFiltro] = useState('manutencao'); // manutencao | pendencia | todos
    const [viewMode, setViewMode] = useState('lista'); // 'lista' | 'timeline'

    // O estado do processo — único estado que existe; a ficha mais recente
    // acompanha-o e o PDF arquivado é regenerado no servidor
    const mudarEstadoObra = async (obra, novo, dataConclusao = null) => {
        if (novo === obra.status) return;
        // Concluir pede a data primeiro; só se grava depois de ela ser confirmada.
        // Sugere o fim da última intervenção registada (hoje, se não houver fichas).
        if (novo === 'concluida' && !dataConclusao) {
            setConcluir({ obra, data: diaInput(obra.ultima_intervencao) || hojeLocal() });
            return;
        }
        try {
            await api.patch(`/manutencao/obras/${obra.id}/estado`,
                { estado: novo, data_conclusao: dataConclusao || undefined });
            setObras(prev => prev.map(o => (o.id === obra.id
                ? { ...o, status: novo, data_conclusao: novo === 'concluida' ? dataConclusao : o.data_conclusao }
                : o)));
            if (aberta === obra.id) carregarFichas(obra.id);
        } catch (e) { notify(e.response?.data?.error || 'Erro ao mudar o estado do processo'); }
    };

    // Pedido da data de conclusão: { obra, data, alterar? } enquanto está a ser
    // pedida. Com alterar, o processo já está concluído e só se corrige a data.
    const [concluir, setConcluir] = useState(null);

    const alterarDataConclusao = async (obra, data) => {
        try {
            const r = await api.patch(`/manutencao/obras/${obra.id}/data-conclusao`, { data_conclusao: data });
            setObras(prev => prev.map(o => (o.id === obra.id ? { ...o, data_conclusao: r.data?.data_conclusao || data } : o)));
        } catch (e) { notify(e.response?.data?.error || 'Erro ao alterar a data de conclusão'); }
    };

    /* Notas próprias, uma por obra. Guardam-se ao carregar no botão e não a
       cada tecla — poupa pedidos e evita gravar meia frase. */
    const [notasEdit, setNotasEdit] = useState({});   // obraId -> texto em edição
    const guardarNotas = async (obra) => {
        const texto = notasEdit[obra.id] ?? obra.notas_manutencao ?? '';
        try {
            await api.patch(`/manutencao/obras/${obra.id}/notas`, { notas: texto });
            setObras(prev => prev.map(o => (o.id === obra.id ? { ...o, notas_manutencao: texto.trim() || null } : o)));
            setNotasEdit(prev => { const n = { ...prev }; delete n[obra.id]; return n; });
        } catch (e) { notify(e.response?.data?.error || 'Erro ao guardar as notas'); }
    };

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/manutencao/obras', { params: { todas: 1, _t: Date.now() } });
            setObras(r.data || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    const carregarFichas = async (obraId) => {
        try {
            const r = await api.get('/manutencao', { params: { obra_id: obraId, _t: Date.now() } });
            setFichas(prev => ({ ...prev, [obraId]: r.data || [] }));
        } catch (e) { console.error(e); }
    };

    const toggleObra = (obraId) => {
        if (aberta === obraId) { setAberta(null); return; }
        setAberta(obraId);
        carregarFichas(obraId);
    };

    const verPdf = async (ficha) => {
        try {
            const res = await api.get(`/manutencao/relatorio/${ficha.id}`, { responseType: 'blob' });
            window.open(window.URL.createObjectURL(res.data), '_blank');
        } catch { notify('Erro ao abrir o PDF'); }
    };

    // Link partilhável (abre o PDF sem login, via share token do relatório)
    const partilharFicha = async (ficha) => {
        const url = `${window.location.origin}/api/manutencao/relatorio/${ficha.id}?t=${ficha.share_token}`;
        try {
            await navigator.clipboard.writeText(url);
            notify(`Link do relatório ${ficha.relatorio_num} copiado:\n\n${url}`);
        } catch {
            window.prompt('Copie o link do relatório:', url);
        }
    };

    const apagarFicha = async (ficha, obraId) => {
        if (!(await confirm(`Apagar o relatório ${ficha.relatorio_num}?\n\nVai para o Lixo e pode ser recuperado.`))) return;
        try {
            const r = await api.delete(`/manutencao/${ficha.id}`);
            notify(r.data.message || 'Relatório movido para o Lixo.');
            carregarFichas(obraId);
            carregar();
        } catch (e) { notify(e.response?.data?.error || 'Erro ao apagar'); }
    };

    const downloadPdf = async (ficha) => {
        try {
            const res = await api.get(`/manutencao/relatorio/${ficha.id}`, { responseType: 'blob' });
            const a = document.createElement('a');
            a.href = window.URL.createObjectURL(res.data);
            a.download = `Manutencao_${ficha.relatorio_num || ficha.id}.pdf`;
            a.click();
        } catch { notify('Erro ao descarregar o PDF'); }
    };

    const nManut = obras.filter(o => (o.tipo_obra || 'manutencao') === 'manutencao').length;
    const nPend = obras.filter(o => o.tipo_obra === 'pendencia').length;

    let visiveis = obras.filter(o => tipoFiltro === 'todos' ? true : (o.tipo_obra || 'manutencao') === tipoFiltro);
    // Quantos processos há em cada estado, dentro do tipo escolhido — o número
    // no botão evita marcar um estado só para descobrir que não tem nada.
    const porEstado = visiveis.reduce((m, o) => {
        const k = o.status || 'planeada';
        m[k] = (m[k] || 0) + 1;
        return m;
    }, {});
    visiveis = visiveis.filter(o => estadosFiltro.has(o.status || 'planeada'));
    if (pesquisa.trim()) {
        const t = pesquisa.trim().toLowerCase();
        visiveis = visiveis.filter(o =>
            (o.posicao || '').toLowerCase().includes(t) || (o.descricao || '').toLowerCase().includes(t) ||
            (o.cliente || '').toLowerCase().includes(t) || (o.observacoes || '').toLowerCase().includes(t));
    }

    // Data de fecho mais recente primeiro; processos sem fecho ficam no fim.
    const dataFecho = obra => {
        if (obra.status !== 'concluida' || !obra.data_conclusao) return -Infinity;
        const data = Date.parse(obra.data_conclusao);
        return Number.isFinite(data) ? data : -Infinity;
    };
    visiveis.sort((a, b) => {
        const dataA = dataFecho(a);
        const dataB = dataFecho(b);
        return dataA === dataB ? 0 : dataA > dataB ? -1 : 1;
    });
    return (
        <div className="page-content manutencoes-page" style={{ padding: 24 }}>
            <div className="mnt-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: '1.4rem' }}><Icon name="maintenance" fallback="🔧" /> Manutenção e Pendências</h1>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.85rem' }}>Processos e fichas de prestação de serviço</p>
                    <div className="mnt-tabs" style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        {[['manutencao', `🔧 Manutenção (${nManut})`], ['pendencia', `📌 Pendências (${nPend})`], ['todos', `Todos (${obras.length})`]].map(([k, l]) => (
                            <button key={k} aria-pressed={tipoFiltro === k} onClick={() => setTipoFiltro(k)}
                                style={{
                                    padding: '6px 14px', borderRadius: 16, fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer',
                                    border: '1px solid ' + (tipoFiltro === k ? '#3182ce' : '#cbd5e1'),
                                    background: tipoFiltro === k ? '#3182ce' : '#fff',
                                    color: tipoFiltro === k ? '#fff' : '#334155',
                                }}><LiquidText>{theme === 'liquido' ? l.replace(/^[🔧📌]\s*/u, '') : l}</LiquidText></button>
                        ))}
                    </div>
                </div>
                <div className="mnt-toolbar" style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    {hasPermission('manutencoes') && <div className="mnt-create-actions">
                        <button type="button" className="mnt-btn mnt-primary" style={btnPri} onClick={() => setNovoTipo('manutencao')}>+ Nova manutenção</button>
                        <button type="button" className="mnt-btn mnt-secondary" style={btnSec} onClick={() => setNovoTipo('pendencia')}>+ Nova pendência</button>
                    </div>}
                    <FiltroEstados seleccionados={estadosFiltro} onAlternar={alternarEstado}
                        contagens={porEstado} abertos={ABERTOS} onDefinir={setEstadosFiltro} />
                    <div>
                        <label style={lbl}>Pesquisar</label>
                        <input value={pesquisa} onChange={e => setPesquisa(e.target.value)} placeholder="Posição, cliente, descritivo…" style={{ ...inp, width: 220 }} />
                    </div>
                    <div className="mnt-view-toggle" style={{ display: 'flex', background: '#e2e8f0', padding: 4, borderRadius: 8, gap: 4 }}>
                        <button aria-pressed={viewMode === 'lista'} onClick={() => setViewMode('lista')} style={segBtn(viewMode === 'lista')} title="Vista em lista"><Icon name="list" fallback="📋" /> Lista</button>
                        <button aria-pressed={viewMode === 'timeline'} onClick={() => setViewMode('timeline')} style={segBtn(viewMode === 'timeline')} title="Vista em linha de tempo (datas)"><Icon name="calendar" fallback="📅" /> Linha de tempo</button>
                    </div>
                    <button onClick={() => carregar()} className="mnt-btn mnt-secondary" style={btnSec}><Icon name="refresh" fallback="🔄" /> Atualizar</button>
                </div>
            </div>

            {ordemLinkExterno && <MaintenanceDialog onClose={() => setOrdemLinkExterno(null)}>
                <ManutencaoLinkExterno obraId={ordemLinkExterno.id} posicao={ordemLinkExterno.posicao} onClose={() => setOrdemLinkExterno(null)} />
            </MaintenanceDialog>}
            {novoTipo && <NovoProcessoManutencao tipo={novoTipo} onClose={() => setNovoTipo(null)} onCreated={obra => {
                setObras(prev => [{ ...obra, num_fichas: 0 }, ...prev]);
                setTipoFiltro(obra.tipo_obra);
                setEstadosFiltro(prev => new Set([...prev, 'planeada']));
                setPesquisa('');
                setCriadoMensagem(`${obra.tipo_obra === 'pendencia' ? 'Pendência' : 'Manutenção'} ${obra.posicao} criada com sucesso.`);
                setNovoTipo(null);
            }} />}
            {criadoMensagem && <p className="mnt-create-success" role="status">{criadoMensagem}</p>}

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : visiveis.length === 0 ? (
                    <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
                        {estadosFiltro.size === 0
                            ? 'Nenhum estado escolhido — marque pelo menos um acima.'
                            : obras.length > 0
                                ? 'Nenhum processo nos estados escolhidos.'
                                : 'Sem processos de manutenção. Crie uma obra com Tipo "Manutenção" no Planeamento.'}
                    </div>
                ) : viewMode === 'timeline' ? (
                    <MaintenanceTimeline obras={visiveis} onAbrirProcesso={(id) => navigate(`/obras/${id}`)} />
                ) : (
                    <div className="mnt-list" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {visiveis.map(o => {
                            const eo = ESTADO_OBRA[o.status] || ESTADO_OBRA.planeada;
                            const lista = fichas[o.id];
                            return (
                                <div className="mnt-process" key={o.id} style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                                    <div className="mnt-process-head" onClick={() => toggleObra(o.id)}
                                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer', flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '1rem', color: '#94a3b8', width: 14 }}>{aberta === o.id ? '▾' : '▸'}</span>
                                        <div style={{ flex: 1, minWidth: 220 }}>
                                            {tipoFiltro === 'todos' && (
                                                <span style={{ fontSize: '0.68rem', fontWeight: 800, marginRight: 6, color: o.tipo_obra === 'pendencia' ? '#7c3aed' : '#2b6cb0' }}>
                                                    <LiquidText>{o.tipo_obra === 'pendencia' ? '📌 PENDÊNCIA' : '🔧 MANUT.'}</LiquidText>
                                                </span>
                                            )}
                                            <strong className="mnt-process-title">{o.posicao}{o.descricao ? ` — ${o.descricao}` : ''}</strong>
                                            {/* Fechado o processo, as datas previstas deixam de valer:
                                                quem lê quer saber quando ficou feito, e isso está na
                                                etiqueta ao lado do estado. Aqui ficam só o cliente e
                                                o responsável. */}
                                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                {o.cliente || '—'}
                                                {o.status !== 'concluida' ? ` · ${fmtData(o.data_inicio)} → ${fmtData(o.data_fim)}` : ''}
                                                {o.responsavel_nome ? ` · Resp.: ${o.responsavel_nome}` : ''}
                                            </div>
                                            {o.observacoes && <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: 3, maxWidth: 640 }} title={o.observacoes}>
                                                <Icon name="document" fallback="📝" /> {(o.observacoes || '').substring(0, 140)}{(o.observacoes || '').length > 140 ? '…' : ''}
                                            </div>}
                                            {o.notas_manutencao && <div style={{ fontSize: '0.8rem', color: '#7c2d12', marginTop: 3, maxWidth: 640 }} title={o.notas_manutencao}>
                                                <Icon name="warning" fallback="📌" /> {o.notas_manutencao.substring(0, 140)}{o.notas_manutencao.length > 140 ? '…' : ''}
                                            </div>}
                                            {/* A data de conclusão passou para a etiqueta ao lado do
                                                estado, onde antes estavam as datas do processo: num
                                                processo fechado é ela que interessa, e repeti-la aqui
                                                era dizer a mesma coisa duas vezes. */}
                                        </div>
                                        {resolucaoInfo(o.status) && (
                                            <span title={resolucaoInfo(o.status).label}
                                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', background: resolucaoInfo(o.status).bg, color: resolucaoInfo(o.status).color, fontWeight: 800, fontSize: '0.9rem', flex: '0 0 auto' }}><LiquidText>✓</LiquidText></span>
                                        )}
                                        {/* As datas do processo aparecem sempre, esteja ele aberto ou fechado:
                                            dizem quando o trabalho decorreu, e isso continua a interessar
                                            depois de fechado — é por elas que se percebe quanto tempo levou.

                                            Fechado, as duas datas ganham rótulo por cima: com duas etiquetas
                                            lado a lado, sem elas ninguém sabe qual é qual. E o calendário sai,
                                            porque o rótulo já diz o que o ícone dizia. Em aberto há uma só
                                            etiqueta, nada com que a confundir, e fica como estava.

                                            Concluído sem data registada fica a amarelo em vez de fingir uma
                                            data: é um buraco que se vê e se corrige. */}
                                        <span className="mnt-process-controls">
                                        {o.status === 'concluida' ? (
                                            <span className="mnt-process-dates" style={{ display: 'inline-flex', gap: 8, alignItems: 'flex-end' }}>
                                                {(o.data_inicio || o.data_fim) && (
                                                    <span style={colunaData}>
                                                        <small style={rotuloData}>Datas do processo</small>
                                                        <span className="mnt-date-chip" style={{ ...chipData, background: '#f1f5f9', color: '#334155' }}>
                                                            {fmtDataCurta(o.data_inicio)}{o.data_fim && String(o.data_fim).slice(0, 10) !== String(o.data_inicio).slice(0, 10) ? ` → ${fmtDataCurta(o.data_fim)}` : ''}
                                                        </span>
                                                    </span>
                                                )}
                                                <span style={colunaData}>
                                                    <small style={rotuloData}>Data conclusão</small>
                                                    {/* Clicar na etiqueta abre a data para a corrigir */}
                                                    <button type="button"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            setConcluir({ obra: o, alterar: true, data: diaInput(o.data_conclusao) || diaInput(o.ultima_intervencao) || hojeLocal() });
                                                        }}
                                                        title={o.data_conclusao
                                                            ? `Concluído em ${fmtData(o.data_conclusao)} — clique para alterar`
                                                            : 'Processo concluído sem data de conclusão registada — clique para a indicar'}
                                                        style={{
                                                            ...chipData, cursor: 'pointer', fontFamily: 'inherit',
                                                            border: `1px solid ${o.data_conclusao ? '#86efac' : '#fcd34d'}`,
                                                            background: o.data_conclusao ? '#dcfce7' : '#fef3c7',
                                                            color: o.data_conclusao ? '#166534' : '#92400e',
                                                        }}><LiquidText>
                                                        ✔ </LiquidText>{o.data_conclusao ? fmtData(o.data_conclusao) : 'sem data'}
                                                        <span aria-hidden="true" style={{ fontSize: '0.7rem', opacity: 0.7 }}><LiquidText>✎</LiquidText></span>
                                                    </button>
                                                </span>
                                            </span>
                                        ) : (o.data_inicio || o.data_fim) && (
                                            <span title="Datas do processo"
                                                className="mnt-date-chip" style={{ ...chipData, background: '#f1f5f9', color: '#334155' }}>
                                                <Icon name="calendar" fallback="📅" /> {fmtDataCurta(o.data_inicio)}{o.data_fim && String(o.data_fim).slice(0, 10) !== String(o.data_inicio).slice(0, 10) ? ` → ${fmtDataCurta(o.data_fim)}` : ''}
                                            </span>
                                        )}
                                        {/* O estado do processo — o único que existe; editável tenha fichas ou não */}
                                        <span style={colunaData}>
                                            <small style={rotuloData}>Estado</small>
                                        <select className="mnt-state" data-state={o.status || 'planeada'} aria-label={`Estado do processo ${o.posicao}`} value={o.status || 'planeada'} onClick={e => e.stopPropagation()}
                                            onChange={e => { e.stopPropagation(); mudarEstadoObra(o, e.target.value); }}
                                            style={{ ...inp, padding: '7px 10px', fontSize: '0.84rem', fontWeight: 700, background: eo.bg, color: eo.fg, borderColor: eo.fg + '55', cursor: 'pointer' }}
                                            title="Estado da pendência/manutenção">
                                            {Object.entries(ESTADO_OBRA)
                                                .filter(([k]) => k !== 'recebida' || o.status === 'recebida')
                                                .map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                        </select>
                                        </span>
                                        <span className="mnt-pill" style={{ ...badge, background: '#f1f5f9', color: '#475569' }}>{o.num_fichas || 0} ficha(s)</span>
                                        <button onClick={e => { e.stopPropagation(); navigate(`/obras/${o.id}`); }} className="mnt-btn mnt-secondary" style={{ ...btnSec, padding: '5px 10px', fontSize: '0.78rem' }} title="Abrir processo da obra">Processo</button>
                                        {hasPermission('manutencoes') && <button type="button" onClick={e => { e.stopPropagation(); setOrdemLinkExterno(o); }} className="mnt-btn mnt-secondary" style={{ ...btnSec, padding: '5px 10px', fontSize: '0.78rem' }}><Icon name="copy" fallback="↗" /> Link para técnico externo</button>}
                                        </span>
                                    </div>

                                    {aberta === o.id && (
                                        <div className="mnt-process-body" style={{ borderTop: '1px solid #f1f5f9', padding: '10px 16px 14px' }}>
                                            <div style={{ marginBottom: 12 }}>
                                                <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b', marginBottom: 4 }}>
                                                    <Icon name="warning" fallback="📌" /> Notas próprias
                                                    <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: 6 }}>— só aqui; não entram em PDF nem em email</span>
                                                </label>
                                                <textarea rows={3}
                                                    value={notasEdit[o.id] ?? o.notas_manutencao ?? ''}
                                                    onChange={e => setNotasEdit(prev => ({ ...prev, [o.id]: e.target.value }))}
                                                    onClick={e => e.stopPropagation()}
                                                    placeholder="Anotações de trabalho sobre esta obra…"
                                                    style={{ ...inp, width: '100%', resize: 'vertical', fontFamily: 'inherit' }} />
                                                {notasEdit[o.id] !== undefined && notasEdit[o.id] !== (o.notas_manutencao ?? '') && (
                                                    <div data-liquid-actions="true" style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                                                        <button onClick={e => { e.stopPropagation(); guardarNotas(o); }} className="mnt-btn mnt-primary" style={btnPri}>Guardar notas</button>
                                                        <button onClick={e => { e.stopPropagation(); setNotasEdit(prev => { const n = { ...prev }; delete n[o.id]; return n; }); }} className="mnt-btn mnt-secondary" style={btnSec}>Cancelar</button>
                                                    </div>
                                                )}
                                            </div>
                                            {!lista ? <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>A carregar fichas…</p>
                                                : lista.length === 0 ? <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '8px 0' }}><LiquidText>Sem fichas registadas — os técnicos criam-nas no mobile (🔧 Manutenção).</LiquidText></p>
                                                    : (
                                                        <table className="standard-table" style={{ width: '100%' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>Relatório</th>
                                                                    <th>Data intervenção</th>
                                                                    <th>Técnico</th>
                                                                    <th>Responsável</th>
                                                                    <th style={{ textAlign: 'right' }}>Ações</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {lista.map(f => {
                                                                    return (
                                                                        <tr key={f.id}>
                                                                            <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{f.relatorio_num}</td>
                                                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                                                {fmtData(f.data_intervencao)}
                                                                                {f.hora_inicio && f.hora_fim ? (
                                                                                    <span style={{ color: '#64748b' }}>
                                                                                        {' '}{horaCurta(f.hora_inicio)}–{horaCurta(f.hora_fim)}
                                                                                        {f.data_fim_intervencao && String(f.data_fim_intervencao).slice(0, 10) !== String(f.data_intervencao).slice(0, 10)
                                                                                            ? ` (${fmtData(f.data_fim_intervencao)})` : ''}
                                                                                    </span>
                                                                                ) : (
                                                                                    <span style={{ color: '#b45309', fontSize: '0.78rem' }} title="Sem hora de início/fim — edite a ficha para as indicar"> · sem horas</span>
                                                                                )}
                                                                            </td>
                                                                            <td>{f.nome_tecnico || '—'}</td>
                                                                            <td>{f.nome_responsavel || '—'}</td>
                                                                            <td>
                                                                                <div data-liquid-actions="true" style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                                                                    <button onClick={() => verPdf(f)} className="mnt-btn mnt-action" style={btnAcao} title="Abrir o PDF arquivado"><Icon name="document" fallback="📄" /> Ver</button>
                                                                                    <button onClick={() => downloadPdf(f)} className="mnt-btn mnt-action" style={btnAcao} title="Descarregar o PDF"><Icon name="download" fallback="⬇" /> Descarregar</button>
                                                                                    <button onClick={() => setEnviarFicha({ ficha: f, obra: o })} className="mnt-btn mnt-action" style={{ ...btnAcao, color: '#1e4e8c', borderColor: '#90cdf4', background: '#ebf8ff' }} title="Enviar o relatório por email ao cliente/fiscalização"><Icon name="mail" fallback="✉️" /> Enviar</button>
                                                                                    <button onClick={() => partilharFicha(f)} className="mnt-btn mnt-action" style={{ ...btnAcao, color: '#166534', borderColor: '#86efac', background: '#f0fff4' }} title="Copiar link partilhável do relatório"><Icon name="copy" fallback="🔗" /> Link</button>
                                                                                    <button onClick={() => setEditarFicha({ ...f, obra_id: o.id })} className="mnt-btn mnt-action" style={btnAcao} title="Editar dados e fotos da ficha"><Icon name="edit" fallback="✏️" /> Editar</button>
                                                                                    <button onClick={() => apagarFicha(f, o.id)} className="mnt-btn mnt-action mnt-danger" style={{ ...btnAcao, color: '#c53030', borderColor: '#feb2b2', background: '#fff5f5' }} title="Apagar (vai para o Lixo, recuperável)"><Icon name="trash" fallback="🗑" /> Apagar</button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

            {editarFicha && (
                <FichaModal ficha={editarFicha} onClose={() => setEditarFicha(null)}
                    onSaved={() => { carregarFichas(editarFicha.obra_id); carregar(); }} />
            )}

            {concluir && (
                <PedidoDataConclusao pedido={concluir}
                    onCancelar={() => setConcluir(null)}
                    onConfirmar={(data) => {
                        const c = concluir;
                        setConcluir(null);
                        if (c.alterar) alterarDataConclusao(c.obra, data);
                        else mudarEstadoObra(c.obra, 'concluida', data);
                    }} />
            )}
            {enviarFicha && (
                <EnviarModal ficha={enviarFicha.ficha} obra={enviarFicha.obra}
                    onClose={() => setEnviarFicha(null)} />
            )}
        </div>
    );
};

/* Caixa de filtro por estado: fecha-se como um select e abre uma lista de
   quadradinhos, um por estado, para escolher um ou vários ao mesmo tempo.
   O rótulo da caixa diz o que está escolhido sem ser preciso abri-la. */
const FiltroEstados = ({ seleccionados, onAlternar, contagens, abertos, onDefinir }) => {
    const [aberto, setAberto] = useState(false);
    const caixa = useRef(null);
    useEffect(() => {
        if (!aberto) return;
        const fora = (e) => { if (caixa.current && !caixa.current.contains(e.target)) setAberto(false); };
        const esc = (e) => { if (e.key === 'Escape') setAberto(false); };
        document.addEventListener('mousedown', fora);
        document.addEventListener('keydown', esc);
        return () => { document.removeEventListener('mousedown', fora); document.removeEventListener('keydown', esc); };
    }, [aberto]);

    const todos = Object.keys(ESTADO_OBRA);
    const n = seleccionados.size;
    const resumo = n === 0 ? 'Nenhum estado'
        : n === todos.length ? 'Todos os estados'
            : n === 1 ? ESTADO_OBRA[[...seleccionados][0]].label
                : `${n} estados`;

    return (
        <div className="mnt-state-filter" ref={caixa} style={{ position: 'relative' }}>
            <label style={lbl}>Estados</label>
            <button aria-expanded={aberto} onClick={() => setAberto(a => !a)} title="Escolher os estados a mostrar"
                style={{ ...inp, width: 200, textAlign: 'left', background: '#fff', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ color: n === 0 ? '#b91c1c' : '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resumo}</span>
                <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>▾</span>
            </button>
            {aberto && (
                <div className="mnt-filter-options" style={{ position: 'absolute', zIndex: 50, top: '100%', left: 0, marginTop: 4, minWidth: 230,
                    background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, boxShadow: '0 8px 24px rgba(15,23,42,0.15)', padding: 6 }}>
                    {Object.entries(ESTADO_OBRA).map(([k, v]) => (
                        <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                            borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', color: '#334155' }}>
                            <input type="checkbox" checked={seleccionados.has(k)} onChange={() => onAlternar(k)} />
                            <span style={{ flex: 1 }}><LiquidText>{v.label}</LiquidText></span>
                            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: v.fg, background: v.bg, borderRadius: 10, padding: '1px 8px' }}>
                                {contagens[k] || 0}
                            </span>
                        </label>
                    ))}
                    <div data-liquid-actions="true" style={{ display: 'flex', gap: 6, borderTop: '1px solid #f1f5f9', marginTop: 4, paddingTop: 6 }}>
                        <button onClick={() => onDefinir(new Set(todos))} className="mnt-btn mnt-secondary" style={{ ...btnSec, flex: 1, padding: '5px 8px', fontSize: '0.76rem' }}>Todos</button>
                        <button onClick={() => onDefinir(new Set(abertos))} className="mnt-btn mnt-secondary" style={{ ...btnSec, flex: 1, padding: '5px 8px', fontSize: '0.76rem' }}>Só os abertos</button>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.72rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
/* Pedido da data de conclusão. Fica por cima de tudo e não deixa concluir
   sem data — cancelar repõe o estado que estava. Com pedido.alterar o
   processo já está concluído e só se corrige a data. A sugestão é o fim da
   última intervenção registada. */
const PedidoDataConclusao = ({ pedido, onConfirmar, onCancelar }) => {
    const [data, setData] = useState(pedido.data);
    const oQue = pedido.obra.tipo_obra === 'pendencia' ? 'a pendência' : 'a manutenção';
    const ultima = diaInput(pedido.obra.ultima_intervencao);
    return (
        <MaintenanceDialog onClose={onCancelar}>
            <div className="mnt-modal mnt-date-dialog" onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: '20px 22px', width: 360, maxWidth: '92vw', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
                <h3 style={{ margin: '0 0 6px', fontSize: '1.05rem' }}>{pedido.alterar ? 'Data de conclusão' : `Concluir ${oQue}`}</h3>
                <p style={{ margin: '0 0 14px', fontSize: '0.86rem', color: '#64748b' }}>
                    <strong>{pedido.obra.posicao}</strong>{pedido.obra.descricao ? ` — ${pedido.obra.descricao}` : ''}
                </p>
                <label htmlFor="mnt-conclusion-date" style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: 4 }}>Data de conclusão</label>
                <input id="mnt-conclusion-date" type="date" value={data} max={hojeLocal()} onChange={e => setData(e.target.value)} autoFocus
                    style={{ ...inp, width: '100%', marginBottom: ultima ? 6 : 16 }} />
                {ultima && (
                    <div style={{ fontSize: '0.76rem', color: '#64748b', marginBottom: 14 }}>
                        Última intervenção terminou a {fmtData(ultima)}
                        {data !== ultima && (
                            <button type="button" onClick={() => setData(ultima)}
                                style={{ border: 'none', background: 'transparent', color: '#2563eb', cursor: 'pointer', fontSize: '0.76rem', padding: '0 0 0 6px' }}>
                                usar esta
                            </button>
                        )}
                    </div>
                )}
                <div data-liquid-actions="true" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onCancelar} className="mnt-btn mnt-secondary" style={btnSec}>Cancelar</button>
                    <button onClick={() => data && onConfirmar(data)} disabled={!data} className="mnt-btn mnt-primary" style={{ ...btnPri, opacity: data ? 1 : 0.5 }}>
                        {pedido.alterar ? 'Guardar' : 'Concluir'}
                    </button>
                </div>
            </div>
        </MaintenanceDialog>
    );
};

const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.76rem', fontWeight: 600, whiteSpace: 'nowrap' };
// A etiqueta da data ao lado do estado: datas previstas enquanto o processo
// corre, data de conclusão depois de fechado. Só muda a cor.
const chipData = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap', flex: '0 0 auto' };
/* Rótulo por cima da data, num processo fechado. Cinzento e miúdo: nomeia a
   etiqueta sem competir com ela. */
const rotuloData = {
    display: 'block', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '.03em',
    textTransform: 'uppercase', color: '#94a3b8', marginBottom: 2, textAlign: 'center',
};
const colunaData = { display: 'inline-flex', flexDirection: 'column', alignItems: 'center' };

const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const segBtn = (active) => ({ padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, background: active ? '#fff' : 'transparent', color: active ? '#1e293b' : '#64748b', boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none' });
const mini = { padding: '4px 9px', marginLeft: 6, fontSize: '0.76rem' };
// Ações de cada ficha: com texto e área de clique confortável
const btnAcao = { padding: '8px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.84rem', fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' };
const fotoRemover = { position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: '50%', border: 'none', background: '#e53e3e', color: '#fff', fontWeight: 'bold', cursor: 'pointer', lineHeight: '20px' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 };

const Manutencoes = () => <OrcamentoFeedbackProvider><ManutencoesContent /></OrcamentoFeedbackProvider>;
export default Manutencoes;
